import { lazy, useState } from 'react';
import { BlockWrapper, defineBlock, smartProps, useEditor, useInspectorControl } from '@smartfire/js-block-engine';
import { EditableText } from '@smartfire/components';
import apiFetch from '@wordpress/api-fetch';
import { CircularProgress } from '@mui/material';
import { ArrowForward, Check } from '@mui/icons-material';
import { __ } from '../shared/tools';
import clsx from 'clsx';
import style from './style.module.scss';

const Inspector = lazy(() => import('./inspector'));

const regexEmail = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;

const Block = ({ title, subtitle, placeholder, backgroundColor, textColor }) => {
    useInspectorControl(Inspector);
    const { isEditing } = useEditor();

    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');

    const handleChangeEmail = (e) => {
        setError(null);
        const newValue = e?.target?.value;
        setEmail(newValue);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (success) return;

        if (!email) {
            setError(__('Merci de renseigner une adresse mail valide.'));
        } else if (!regexEmail.test(email)) {
            setError(__("L'adresse mail renseignée est invalide."));
        } else {
            setLoading(true);
            apiFetch({
                path: 'paraboot/client/newsletter',
                method: 'POST',
                data: { email },
            })
                .then((data) => {
                    if (data.success) {
                        setSuccess(true);
                    } else {
                        setError(__("Une erreur est survenue lors de l'inscription à la newsletter."));
                    }
                })
                .catch((err) => {
                    if (err?.errors?.length > 0) {
                        setError(__("Une erreur est survenue lors de l'inscription à la newsletter."));
                    }
                })
                .finally(() => setLoading(false));
        }
    };

    return (
        <BlockWrapper.Div
            className={style.container}
            style={{ '--background-color': backgroundColor, '--text-color': textColor }}
        >
            <div className={style.newsletter}>
                {(isEditing || subtitle) && (
                    <EditableText
                        tagName="p"
                        smartProp="subtitle"
                        placeholder="restez au courant de nos collections"
                        className={style.subtitle}
                    />
                )}
                {(isEditing || title) && (
                    <EditableText tagName="p" smartProp="title" placeholder="Newsletter" className={style.title} />
                )}

                <form className={style.actionContainer} onSubmit={handleSubmit}>
                    <div className={style.inputContainer}>
                        <input
                            type="email"
                            value={email}
                            onChange={handleChangeEmail}
                            className={style.input}
                            placeholder={placeholder}
                        />
                    </div>

                    <button
                        type="submit"
                        className={clsx(style.button, loading && style.loading, success && style.success)}
                        disabled={loading || success}
                    >
                        {loading ? (
                            <CircularProgress size={20} className={style.icon} />
                        ) : success ? (
                            <Check className={style.icon} />
                        ) : (
                            <ArrowForward className={style.icon} />
                        )}
                    </button>
                </form>

                {error && <p className={style.error}>{error}</p>}
                {success && (
                    <p className={style.confirmation}>{__('Vous avez bien été inscrit à notre newsletter, merci !')}</p>
                )}
            </div>
        </BlockWrapper.Div>
    );
};

export default defineBlock({
    Block,
    smartProps: {
        subtitle: smartProps.string.withDefault(''),
        title: smartProps.string.withDefault(''),
        placeholder: smartProps.string.withDefault(''),
        backgroundColor: smartProps.string.withDefault('#0a2c2b'),
        textColor: smartProps.string.withDefault('#ffffff'),
    },
    setup: {
        title: 'Newsletter',
    },
});
